import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ShoppingCart, 
  Package, 
  Truck, 
  CheckCircle, 
  Clock, 
  Search, 
  Filter, 
  Plus, 
  Eye, 
  Edit, 
  Trash2, 
  Check, 
  X, 
  X as CloseIcon,
  Building2,
  User,
  Users,
  Calendar,
  DollarSign,
  FileText,
  Home,
  Phone,
  Mail,
  Tag,
  BarChart3,
  Receipt,
  Layers,
  ShoppingBag,
  ArrowLeft,
  ArrowRight,
  Loader2,
  ArrowLeft as BackIcon,
  RotateCcw,
  CreditCard,
  Calculator,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import Layout from '../components/Layout';
import toast from 'react-hot-toast';
import apiService from '../services/apiService';

const ReturnPurchases = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { formatCurrency, currencySymbol } = useSettings();
  
  // View modal state
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewData, setViewData] = useState(null);
  
  // Bulk return modal state
  const [showBulkReturnModal, setShowBulkReturnModal] = useState(false);
  const [returnReason, setReturnReason] = useState('');
  const [bulkReturnItems, setBulkReturnItems] = useState([]);
  
  // Individual return modal state
  const [showIndividualReturnModal, setShowIndividualReturnModal] = useState(false);
  const [selectedPurchaseForReturn, setSelectedPurchaseForReturn] = useState(null);
  const [returnItems, setReturnItems] = useState([]);
  
  // Expanded purchase orders state
  const [expandedPurchases, setExpandedPurchases] = useState(new Set());
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [suppliers, setSuppliers] = useState([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Bulk selection state
  const [selectedPurchases, setSelectedPurchases] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [bulkReturnLoading, setBulkReturnLoading] = useState(false);



  // Fetch approved and received purchases for return
  const fetchApprovedPurchases = async () => {
    try {
      setLoading(true);
      
      // Fetch both approved and received purchases
      const params = new URLSearchParams({
        page: currentPage,
        per_page: perPage,
        ...(searchTerm && { search: searchTerm }),
        ...(supplierFilter && { supplier_id: supplierFilter })
      });

      // Fetch approved purchases
      const approvedResponse = await apiService.request(`/purchases/approved?${params}`);
      
      // Fetch received purchases  
      const receivedResponse = await apiService.request(`/purchases/received?${params}`);
      
      // Combine both results
      const allPurchases = [
        ...(approvedResponse.data.data || []),
        ...(receivedResponse.data.data || [])
      ];
      
      setPurchases(allPurchases);
      setTotalPages(Math.max(approvedResponse.data.last_page || 0, receivedResponse.data.last_page || 0));
      setTotalItems((approvedResponse.data.total || 0) + (receivedResponse.data.total || 0));
      setError(null);
    } catch (err) {
      setError('Failed to fetch approved purchases');
      console.error('Error fetching approved purchases:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch suppliers
  const fetchSuppliers = async () => {
    try {
      const response = await apiService.request('/suppliers');
      setSuppliers(response.data);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
    }
  };

  // Effects
  useEffect(() => {
    fetchApprovedPurchases();
  }, [currentPage, perPage, searchTerm, supplierFilter]);

  useEffect(() => {
    fetchSuppliers();
  }, []);

  // Update selectAll state when individual selections change
  useEffect(() => {
    setSelectAll(selectedPurchases.length === purchases.length && purchases.length > 0);
  }, [selectedPurchases, purchases]);

  // Handlers

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleSupplierFilter = (supplierId) => {
    setSupplierFilter(supplierId);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
  };

  const handleReturnPurchase = async (purchaseId) => {
    // Find the purchase to open the individual return modal
    const purchase = purchases.find(p => p.id === purchaseId);
    if (purchase) {
      // Check if purchase has items to return
      if (!purchase.items || purchase.items.length === 0) {
        toast.error('This purchase has no items to return.');
        return;
      }
      handleOpenIndividualReturnModal(purchase);
    }
  };

  const handleIndividualReturn = async () => {
    if (!returnReason || returnReason.trim() === '') {
      toast.error('Return reason is required.');
      return;
    }

    // Filter items that have return quantities > 0
    const itemsToReturn = returnItems.filter(item => item.return_quantity > 0);
    
    if (itemsToReturn.length === 0) {
      toast.error('Please select at least one item to return.');
      return;
    }

    // Validate that return quantities don't exceed available quantities
    const invalidItems = itemsToReturn.filter(item => {
      const availableInPieces = item.available_quantity * item.pieces_in_ctn;
      const totalQtyInPieces = item.qty_pcs + (item.qty_ctn * item.pieces_in_ctn);
      return totalQtyInPieces > availableInPieces;
    });

    if (invalidItems.length > 0) {
      toast.error('Some items have return quantities exceeding available stock. Please adjust the quantities.');
      return;
    }

    try {
      await apiService.request(`/purchases/${selectedPurchaseForReturn.id}/return`, {
        method: 'POST',
        body: JSON.stringify({
          return_reason: returnReason.trim(),
          items: itemsToReturn.map(item => ({
            id: item.id,
            quantity: item.return_quantity,
            qty_pcs: item.qty_pcs,
            qty_ctn: item.qty_ctn
          }))
        })
      });
      
      // Close modal and refresh the list
      handleCloseIndividualReturnModal();
      fetchApprovedPurchases();
      
      toast.success(`Successfully returned ${itemsToReturn.length} item(s)!`);
    } catch (err) {
      console.error('Error returning purchase:', err);
      toast.error('Failed to return purchase. Please try again.');
    }
  };

  // Bulk return handler
  const handleBulkReturn = async () => {
    if (!returnReason || returnReason.trim() === '') {
      toast.error('Return reason is required.');
      return;
    }

    // Filter items that have return quantities > 0
    const itemsToReturn = bulkReturnItems.filter(item => item.return_quantity > 0);
    
    if (itemsToReturn.length === 0) {
      toast.error('Please select at least one item to return.');
      return;
    }

    // Validate that return quantities don't exceed available quantities
    const invalidItems = itemsToReturn.filter(item => {
      const availableInPieces = item.available_quantity * item.pieces_in_ctn;
      const totalQtyInPieces = item.qty_pcs + (item.qty_ctn * item.pieces_in_ctn);
      return totalQtyInPieces > availableInPieces;
    });

    if (invalidItems.length > 0) {
      toast.error('Some items have return quantities exceeding available stock. Please adjust the quantities.');
      return;
    }

    // Group items by purchase ID
    const itemsByPurchase = itemsToReturn.reduce((acc, item) => {
      if (!acc[item.purchase_id]) {
        acc[item.purchase_id] = [];
      }
      acc[item.purchase_id].push({
        id: item.id,
        quantity: item.return_quantity,
        qty_pcs: item.qty_pcs,
        qty_ctn: item.qty_ctn
      });
      return acc;
    }, {});

    try {
      setBulkReturnLoading(true);
      
      // Return items for each purchase
      const returnPromises = Object.entries(itemsByPurchase).map(([purchaseId, items]) =>
        apiService.request(`/purchases/${purchaseId}/return`, {
          method: 'POST',
          data: {
            return_reason: returnReason.trim(),
            items: items
          }
        })
      );

      await Promise.all(returnPromises);
      
      // Clear selection and refresh the list
      setSelectedPurchases([]);
      setSelectAll(false);
      setShowBulkReturnModal(false);
      setReturnReason('');
      setBulkReturnItems([]);
      fetchApprovedPurchases();
      
      toast.success(`Successfully returned ${itemsToReturn.length} item(s) from ${Object.keys(itemsByPurchase).length} purchase(s)!`);
    } catch (error) {
      console.error('Error returning purchases:', error);
      toast.error('Failed to return some purchases. Please try again.');
    } finally {
      setBulkReturnLoading(false);
    }
  };

  // Handle individual purchase selection
  const handlePurchaseSelect = (purchaseId) => {
    setSelectedPurchases(prev => {
      if (prev.includes(purchaseId)) {
        return prev.filter(id => id !== purchaseId);
      } else {
        return [...prev, purchaseId];
      }
    });
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedPurchases([]);
      setSelectAll(false);
    } else {
      setSelectedPurchases(purchases.map(p => p.id));
      setSelectAll(true);
    }
  };

  // View modal handlers
  const handleOpenViewModal = (purchaseData) => {
    setViewData(purchaseData);
    setShowViewModal(true);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setViewData(null);
  };

  // Bulk return modal handlers
  const handleOpenBulkReturnModal = () => {
    if (selectedPurchases.length === 0) {
      toast.error('Please select at least one purchase to return.');
      return;
    }
    
    // Initialize bulk return items from all selected purchases
    const allItems = [];
    selectedPurchases.forEach(purchaseId => {
      const purchase = purchases.find(p => p.id === purchaseId);
      if (purchase && purchase.items) {
        purchase.items.forEach(item => {
          allItems.push({
            id: item.id,
            purchase_id: purchaseId,
            purchase_order_number: purchase.purchase_order_number,
            supplier_name: purchase.supplier?.name || 'Unknown',
            product_id: item.product_id,
            product_name: item.product?.name || 'Unknown Product',
            available_quantity: item.quantity,
            return_quantity: 0,
            qty_pcs: 0,
            qty_ctn: 0,
            pieces_in_ctn: item.product?.pieces_in_ctn || 1,
            unit_cost: item.cost_price,
            total_cost: 0
          });
        });
      }
    });
    
    setBulkReturnItems(allItems);
    setShowBulkReturnModal(true);
  };

  const handleCloseBulkReturnModal = () => {
    setShowBulkReturnModal(false);
    setReturnReason('');
    setBulkReturnItems([]);
  };

  // Individual return modal handlers
  const handleOpenIndividualReturnModal = (purchase) => {
    setSelectedPurchaseForReturn(purchase);
    // Initialize return items with all items set to 0 quantity
    const initialReturnItems = purchase.items?.map(item => ({
      id: item.id,
      product_id: item.product_id,
      product_name: item.product?.name || 'Unknown Product',
      available_quantity: item.quantity,
      return_quantity: 0,
      qty_pcs: 0,
      qty_ctn: 0,
      pieces_in_ctn: item.product?.pieces_in_ctn || 1,
      unit_cost: item.cost_price,
      total_cost: 0
    })) || [];
    
    // Additional safety check
    if (initialReturnItems.length === 0) {
      toast.error('This purchase has no items to return.');
      return;
    }
    
    setReturnItems(initialReturnItems);
    setShowIndividualReturnModal(true);
  };

  const handleCloseIndividualReturnModal = () => {
    setShowIndividualReturnModal(false);
    setSelectedPurchaseForReturn(null);
    setReturnItems([]);
    setReturnReason('');
  };

  const handleReturnQuantityChange = (itemId, field, value) => {
    setReturnItems(prev => prev.map(item => {
      if (item.id === itemId) {
        let newQtyPcs = item.qty_pcs;
        let newQtyCtn = item.qty_ctn;
        
        if (field === 'qty_pcs') {
          newQtyPcs = Math.max(0, value);
          // Check if total exceeds available and limit PCS accordingly
          const availableInPieces = item.available_quantity * item.pieces_in_ctn;
          const totalWithNewPcs = newQtyPcs + (item.qty_ctn * item.pieces_in_ctn);
          if (totalWithNewPcs > availableInPieces) {
            newQtyPcs = Math.max(0, availableInPieces - (item.qty_ctn * item.pieces_in_ctn));
          }
        } else if (field === 'qty_ctn') {
          newQtyCtn = Math.max(0, value);
          // Check if total exceeds available and limit CTN accordingly
          const availableInPieces = item.available_quantity * item.pieces_in_ctn;
          const totalWithNewCtn = item.qty_pcs + (newQtyCtn * item.pieces_in_ctn);
          if (totalWithNewCtn > availableInPieces) {
            newQtyCtn = Math.floor((availableInPieces - item.qty_pcs) / item.pieces_in_ctn);
          }
        }
        
        // Calculate total quantity in pieces for validation
        const availableInPieces = item.available_quantity * item.pieces_in_ctn;
        const totalQtyInPieces = newQtyPcs + (newQtyCtn * item.pieces_in_ctn);
        const returnQty = Math.min(totalQtyInPieces, availableInPieces);
        
        // Calculate total quantity in cartons (convert PCS to CTN first, then add to CTN)
        const pcsAsCartons = newQtyPcs / item.pieces_in_ctn;
        const totalQtyInCartons = pcsAsCartons + newQtyCtn;
        
        return {
          ...item,
          qty_pcs: newQtyPcs,
          qty_ctn: newQtyCtn,
          return_quantity: returnQty,
          total_qty: totalQtyInCartons, // Total in cartons (can be fractional)
          total_cost: returnQty * item.unit_cost
        };
      }
      return item;
    }));
  };

  const handleBulkReturnQuantityChange = (itemId, field, value) => {
    setBulkReturnItems(prev => prev.map(item => {
      if (item.id === itemId) {
        let newQtyPcs = item.qty_pcs;
        let newQtyCtn = item.qty_ctn;
        
        if (field === 'qty_pcs') {
          newQtyPcs = Math.max(0, value);
          // Check if total exceeds available and limit PCS accordingly
          const availableInPieces = item.available_quantity * item.pieces_in_ctn;
          const totalWithNewPcs = newQtyPcs + (item.qty_ctn * item.pieces_in_ctn);
          if (totalWithNewPcs > availableInPieces) {
            newQtyPcs = Math.max(0, availableInPieces - (item.qty_ctn * item.pieces_in_ctn));
          }
        } else if (field === 'qty_ctn') {
          newQtyCtn = Math.max(0, value);
          // Check if total exceeds available and limit CTN accordingly
          const availableInPieces = item.available_quantity * item.pieces_in_ctn;
          const totalWithNewCtn = item.qty_pcs + (newQtyCtn * item.pieces_in_ctn);
          if (totalWithNewCtn > availableInPieces) {
            newQtyCtn = Math.floor((availableInPieces - item.qty_pcs) / item.pieces_in_ctn);
          }
        }
        
        // Calculate total quantity in pieces for validation
        const availableInPieces = item.available_quantity * item.pieces_in_ctn;
        const totalQtyInPieces = newQtyPcs + (newQtyCtn * item.pieces_in_ctn);
        const returnQty = Math.min(totalQtyInPieces, availableInPieces);
        
        // Calculate total quantity in cartons (convert PCS to CTN first, then add to CTN)
        const pcsAsCartons = newQtyPcs / item.pieces_in_ctn;
        const totalQtyInCartons = pcsAsCartons + newQtyCtn;
        
        return {
          ...item,
          qty_pcs: newQtyPcs,
          qty_ctn: newQtyCtn,
          return_quantity: returnQty,
          total_qty: totalQtyInCartons, // Total in cartons (can be fractional)
          total_cost: returnQty * item.unit_cost
        };
      }
      return item;
    }));
  };

  // Toggle expanded state for purchase orders
  const togglePurchaseExpansion = (purchaseId) => {
    setExpandedPurchases(prev => {
      const newSet = new Set(prev);
      if (newSet.has(purchaseId)) {
        newSet.delete(purchaseId);
      } else {
        newSet.add(purchaseId);
      }
      return newSet;
    });
  };


  return (
    <Layout>
      <div className="space-y-6">
          {/* Hero Section */}
          <div className="relative overflow-hidden mt-20 bg-gradient-to-br from-orange-50 via-white to-red-50 rounded-3xl p-8 mb-8 shadow-2xl border border-orange-200">
            {/* Background Pattern */}
            <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-white to-red-50 opacity-50"></div>
            <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-br from-orange-100 to-red-100 rounded-full -translate-y-24 translate-x-24 opacity-30"></div>
            <div className="absolute bottom-0 left-0 w-36 h-36 bg-gradient-to-tr from-red-100 to-orange-100 rounded-full translate-y-18 -translate-x-18 opacity-30"></div>
            
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-6">
                  <div className="relative">
                    <div className="w-16 h-16 bg-gradient-to-br from-orange-500 via-red-500 to-pink-600 rounded-2xl flex items-center justify-center shadow-2xl transform rotate-3 hover:rotate-0 transition-transform duration-300">
                      <RotateCcw size={32} className="text-white" />
                    </div>
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center shadow-lg">
                      <X size={12} className="text-white" />
                    </div>
                  </div>
                  <div>
                    <h1 className="text-4xl font-bold bg-gradient-to-r from-orange-600 via-red-600 to-pink-600 bg-clip-text text-transparent mb-2">
                      Return Purchases
                    </h1>
                    <p className="text-gray-600 text-lg font-medium">
                      Return approved purchase orders to suppliers
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={handleOpenBulkReturnModal}
                    disabled={selectedPurchases.length === 0 || bulkReturnLoading}
                    className={`px-6 py-3 text-white rounded-xl transition-all duration-200 flex items-center gap-2 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 ${
                      selectedPurchases.length === 0 || bulkReturnLoading
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700'
                    }`}
                  >
                    {bulkReturnLoading ? (
                      <Loader2 size={20} className="animate-spin" />
                    ) : (
                    <RotateCcw size={20} />
                    )}
                    {bulkReturnLoading ? 'Returning...' : `Bulk Return (${selectedPurchases.length})`}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Total Received */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-600 mb-1">Total Received</p>
                  <p className="text-3xl font-bold text-green-900">
                    {loading ? <Loader2 className="animate-spin" size={24} /> : purchases.length || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
                  <CheckCircle className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            {/* Available to Return */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-orange-600 mb-1">Available to Return</p>
                  <p className="text-3xl font-bold text-orange-900">
                    {loading ? <Loader2 className="animate-spin" size={24} /> : purchases.filter(p => p.status === 'received').length || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                  <RotateCcw className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            {/* Total Value */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-600 mb-1">Total Value</p>
                  <p className="text-3xl font-bold text-purple-900">
                    {loading ? <Loader2 className="animate-spin" size={24} /> : formatCurrency(purchases.reduce((sum, p) => {
                      const cost = Number(p.total_cost) || 0;
                      return sum + (isNaN(cost) ? 0 : cost);
                    }, 0))}
                  </p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                  <DollarSign className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            {/* Suppliers */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-600 mb-1">Suppliers</p>
                  <p className="text-3xl font-bold text-blue-900">
                    {loading ? <Loader2 className="animate-spin" size={24} /> : new Set(purchases.map(p => p.supplier_id)).size || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Building2 className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Filters and Search */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-6">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by order number, supplier, or invoice..."
                    value={searchTerm}
                    onChange={handleSearch}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                  />
                </div>
              </div>

              {/* Supplier Filter */}
              <div className="lg:w-48">
                <select
                  value={supplierFilter}
                  onChange={(e) => handleSupplierFilter(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                >
                  <option value="">All Suppliers</option>
                  {suppliers.map((supplier) => (
                    <option key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Clear Filters */}
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSupplierFilter('');
                  setCurrentPage(1);
                }}
                className="px-6 py-3 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 rounded-xl hover:from-gray-200 hover:to-gray-300 transition-all duration-200 flex items-center gap-2 font-semibold shadow-sm hover:shadow-md transform hover:scale-105"
              >
                <X size={16} />
                Clear
              </button>
            </div>
          </div>

          {/* Approved Purchases Table */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
              <h3 className="text-lg font-semibold text-gray-900">Received Purchase Orders</h3>
              <p className="text-sm text-gray-500">Return purchase orders to suppliers</p>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="animate-spin text-red-500" size={32} />
                <span className="ml-3 text-gray-600">Loading approved purchases...</span>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <X className="mx-auto text-red-500" size={48} />
                  <p className="mt-2 text-gray-600">{error}</p>
                  <button
                    onClick={fetchApprovedPurchases}
                    className="mt-4 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                  >
                    Retry
                  </button>
                </div>
              </div>
            ) : purchases.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <CheckCircle className="mx-auto text-green-500" size={48} />
                  <p className="mt-2 text-gray-600 text-lg">No approved purchases</p>
                  <p className="text-gray-400">No purchase orders available for return</p>
                </div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={handleSelectAll}
                            className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                          />
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order #</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Supplier</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Cost</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expected Delivery</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Approved Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {purchases.map((purchase) => (
                        <React.Fragment key={purchase.id}>
                          <tr className="hover:bg-gradient-to-r hover:from-gray-50 hover:to-white transition-all duration-200">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={selectedPurchases.includes(purchase.id)}
                              onChange={() => handlePurchaseSelect(purchase.id)}
                              className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <button
                                onClick={() => togglePurchaseExpansion(purchase.id)}
                                className="mr-2 p-1 hover:bg-gray-100 rounded transition-colors"
                                title={expandedPurchases.has(purchase.id) ? "Hide items" : "Show items"}
                              >
                                {expandedPurchases.has(purchase.id) ? (
                                  <ChevronDown size={16} className="text-gray-500" />
                                ) : (
                                  <ChevronRight size={16} className="text-gray-500" />
                                )}
                              </button>
                              <div className="w-10 h-10 bg-gradient-to-br from-orange-100 to-red-100 rounded-full flex items-center justify-center">
                                <ShoppingCart size={16} className="text-orange-600" />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">{purchase.purchase_order_number}</div>
                                {purchase.supplier_invoice_number && (
                                  <div className="text-sm text-gray-500">Invoice: {purchase.supplier_invoice_number}</div>
                                )}
                                {purchase.items && (
                                  <div className="text-xs text-gray-400">
                                    {purchase.items.length} item{purchase.items.length !== 1 ? 's' : ''}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{purchase.supplier?.name || 'N/A'}</div>
                            <div className="text-sm text-gray-500">{purchase.contact_person || 'N/A'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{formatCurrency(purchase.total_cost)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {purchase.expected_delivery_date 
                                ? new Date(purchase.expected_delivery_date).toLocaleDateString()
                                : 'N/A'
                              }
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {new Date(purchase.updated_at).toLocaleDateString()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleOpenViewModal(purchase)}
                                className="text-blue-600 hover:text-blue-900 p-2 rounded-lg hover:bg-blue-50 transition-all duration-200"
                                title="View Details"
                              >
                                <Eye size={16} />
                              </button>
                              <button
                                onClick={() => handleReturnPurchase(purchase.id)}
                                disabled={!purchase.items || purchase.items.length === 0}
                                className={`p-2 rounded-lg transition-all duration-200 ${
                                  !purchase.items || purchase.items.length === 0
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-orange-600 hover:text-orange-900 hover:bg-orange-50'
                                }`}
                                title={
                                  !purchase.items || purchase.items.length === 0
                                    ? 'No items to return'
                                    : 'Return Purchase'
                                }
                              >
                                <RotateCcw size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                        {/* Expanded Items Row */}
                        {expandedPurchases.has(purchase.id) && (
                          <tr className="bg-gray-50">
                            <td colSpan={7} className="px-6 py-4">
                              <div className="bg-white rounded-lg border border-gray-200 p-4">
                                <div className="flex items-center justify-between mb-4">
                                  <h4 className="text-lg font-semibold text-gray-900">
                                    Purchase Order Items
                                  </h4>
                                  <div className="text-sm text-gray-500">
                                    Total: {purchase.items?.length || 0} items
                                  </div>
                                </div>
                                
                                {purchase.items && purchase.items.length > 0 ? (
                                  <div className="space-y-3">
                                    {purchase.items.map((item, index) => (
                                      <div key={item.id || index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-center">
                                          <div className="md:col-span-2">
                                            <div className="flex items-center space-x-3">
                                              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                                                <Package size={16} className="text-white" />
                                              </div>
                                              <div>
                                                <h5 className="font-medium text-gray-900">
                                                  {item.product?.name || 'Unknown Product'}
                                                </h5>
                                                <p className="text-sm text-gray-500">
                                                  ID: {item.product_id || 'N/A'}
                                                </p>
                                              </div>
                                            </div>
                                          </div>
                                          
                                          <div className="text-center">
                                            <p className="text-sm font-medium text-gray-900">
                                              {item.quantity}
                                            </p>
                                            <p className="text-xs text-gray-500">Quantity</p>
                                          </div>
                                          
                                          <div className="text-center">
                                            <p className="text-sm font-medium text-gray-900">
                                              {item.uom || 'pcs'}
                                            </p>
                                            <p className="text-xs text-gray-500">Unit</p>
                                          </div>
                                          
                                          <div className="text-center">
                                            <p className="text-sm font-medium text-gray-900">
                                              {formatCurrency(item.cost_price)}
                                            </p>
                                            <p className="text-xs text-gray-500">Unit Cost</p>
                                          </div>
                                          
                                          <div className="text-center">
                                            <p className="text-lg font-bold text-green-600">
                                              {formatCurrency(item.subtotal || (item.quantity * item.cost_price))}
                                            </p>
                                            <p className="text-xs text-gray-500">Total</p>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                    
                                    {/* Items Summary */}
                                    <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4 border border-green-200 mt-4">
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <h5 className="font-semibold text-gray-900">Items Summary</h5>
                                          <p className="text-sm text-gray-600">
                                            {purchase.items.length} items • Total Quantity: {purchase.items.reduce((sum, item) => sum + (item.quantity || 0), 0)}
                                          </p>
                                        </div>
                                        <div className="text-right">
                                          <p className="text-xl font-bold text-green-600">
                                            {formatCurrency(purchase.items.reduce((sum, item) => sum + (item.subtotal || (item.quantity * item.cost_price)), 0))}
                                          </p>
                                          <p className="text-sm text-gray-500">Total Value</p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="text-center py-8">
                                    <Package size={48} className="mx-auto text-gray-400 mb-4" />
                                    <p className="text-gray-500">No items found for this purchase order</p>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="bg-gradient-to-r from-gray-50 to-white px-6 py-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-gray-700">
                        Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, totalItems)} of {totalItems} results
                      </span>
                      <select
                        value={perPage}
                        onChange={(e) => handlePerPageChange(Number(e.target.value))}
                        className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      >
                        <option value={10}>10 per page</option>
                        <option value={15}>15 per page</option>
                        <option value={25}>25 per page</option>
                        <option value={50}>50 per page</option>
                      </select>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-4 py-2 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                      >
                        <ArrowLeft size={16} className="inline mr-1" />
                        Previous
                      </button>
                      <span className="px-4 py-2 text-sm text-gray-700 bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl font-medium">
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-4 py-2 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                      >
                        Next
                        <ArrowRight size={16} className="inline ml-1" />
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

      {/* View Modal */}
      {showViewModal && viewData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <Eye size={20} />
                </div>
                <div>
                  <h2 className="text-xl font-bold">Purchase Order Details</h2>
                  <p className="text-blue-100 text-sm">PO: {viewData.purchase_order_number}</p>
                </div>
              </div>
              <button
                onClick={handleCloseViewModal}
                className="p-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors"
              >
                <CloseIcon size={16} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Company Header */}
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-blue-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Receipt size={32} className="text-white" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">NEXAPOS</h1>
                <p className="text-gray-600">Purchase Order Details</p>
              </div>

              {/* Order Details */}
              <div className="grid grid-cols-2 gap-6 mb-6">
                {/* Company Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Company Information</h3>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Company:</span>
                      <span className="text-gray-900 font-medium">NEXAPOS</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Order Date:</span>
                      <span className="text-gray-900 font-medium">{new Date(viewData.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Received Date:</span>
                      <span className="text-gray-900 font-medium">{new Date(viewData.updated_at).toLocaleDateString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Agent:</span>
                      <span className="text-gray-900 font-medium">System User</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Status:</span>
                      <span className="text-gray-900 font-medium capitalize">{viewData.status}</span>
                    </div>
                  </div>
                </div>

                {/* Supplier Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Supplier Information</h3>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Supplier:</span>
                      <span className="text-gray-900 font-medium">{viewData.supplier?.name || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Supplier ID:</span>
                      <span className="text-gray-900 font-medium">{viewData.supplier?.supplierID || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Phone:</span>
                      <span className="text-gray-900 font-medium">{viewData.supplier?.phone || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Email:</span>
                      <span className="text-gray-900 font-medium">{viewData.supplier?.email || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Address:</span>
                      <span className="text-gray-900 font-medium">{viewData.supplier?.address || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Items</h3>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                    <div className="grid grid-cols-6 gap-4 text-sm font-semibold text-gray-700 uppercase tracking-wide">
                      <div>Product</div>
                      <div className="text-center">Quantity</div>
                      <div className="text-center">UOM</div>
                      <div className="text-right">Price</div>
                      <div className="text-right">Amount</div>
                      <div className="text-center">Status</div>
                    </div>
                  </div>
                  <div className="divide-y divide-gray-200">
                    {viewData.items?.map((item, index) => (
                      <div key={index} className="px-4 py-3">
                        <div className="grid grid-cols-6 gap-4 items-center text-sm">
                          <div className="font-medium text-gray-900">
                            {item.product?.name || 'Unknown Product'}
                          </div>
                          <div className="text-center">
                            <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                              {item.quantity}
                            </span>
                          </div>
                          <div className="text-center text-gray-600">
                            {item.product?.unit_of_measure || 'pcs'}
                          </div>
                          <div className="text-right font-medium text-gray-900">
                            {formatCurrency(Number(item.cost_price) || 0)}
                          </div>
                          <div className="text-right font-bold text-gray-900">
                            {formatCurrency(Number(item.subtotal) || 0)}
                          </div>
                          <div className="text-center">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              <CheckCircle size={12} className="mr-1" />
                              Received
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Order Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-gray-900">Total Amount:</span>
                  <span className="text-2xl font-bold text-gray-900">
                    {formatCurrency(Number(viewData.total_cost) || 0)}
                  </span>
                </div>
              </div>

              {/* Order Status */}
              <div className="mt-6 text-center">
                <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                  <CheckCircle size={16} className="mr-2" />
                  Order Ready for Return
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 p-4 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Order viewed on {new Date().toLocaleString()}
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => handleReturnPurchase(viewData.id)}
                  disabled={!viewData.items || viewData.items.length === 0}
                  className={`px-4 py-2 rounded-lg flex items-center gap-2 font-semibold transition-all duration-200 ${
                    !viewData.items || viewData.items.length === 0
                      ? 'bg-gray-400 cursor-not-allowed text-white'
                      : 'bg-orange-600 hover:bg-orange-700 text-white'
                  }`}
                  title={
                    !viewData.items || viewData.items.length === 0
                      ? 'No items to return'
                      : 'Return Purchase'
                  }
                >
                  <RotateCcw size={16} />
                  Return Purchase
                </button>
                <button
                  onClick={handleCloseViewModal}
                  className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg flex items-center gap-2 font-semibold transition-all duration-200"
                >
                  <CloseIcon size={16} />
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Return Modal */}
      {showBulkReturnModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[95vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white p-6 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <RotateCcw size={24} />
    </div>
                <div>
                  <h2 className="text-2xl font-bold">Bulk Return Purchases</h2>
                  <p className="text-orange-100 text-sm">
                    Returning {selectedPurchases.length} purchase order(s)
                  </p>
                </div>
              </div>
              <button
                onClick={handleCloseBulkReturnModal}
                className="p-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors"
              >
                <CloseIcon size={20} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Selected Purchases Summary */}
              <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl p-4 mb-6 border border-orange-200">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-600 rounded-lg flex items-center justify-center">
                    <ShoppingCart size={20} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {selectedPurchases.length} Purchase Order(s) Selected
                    </h3>
                    <p className="text-sm text-gray-600">
                      Select specific items to return from the purchases below
                    </p>
                  </div>
                </div>
              </div>

              {/* Return Reason Form */}
              <div className="mb-6">
                <label htmlFor="bulkReturnReason" className="block text-sm font-medium text-gray-700 mb-2">
                  Return Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="bulkReturnReason"
                  value={returnReason}
                  onChange={(e) => setReturnReason(e.target.value)}
                  placeholder="Please provide a detailed reason for returning these items..."
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 resize-none"
                />
              </div>

              {/* Items Selection */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Items to Return</h3>
                
                {bulkReturnItems.length > 0 ? (
                  <div className="space-y-4">
                    {/* Group items by purchase order */}
                    {Object.entries(
                      bulkReturnItems.reduce((acc, item) => {
                        if (!acc[item.purchase_order_number]) {
                          acc[item.purchase_order_number] = {
                            supplier: item.supplier_name,
                            items: []
                          };
                        }
                        acc[item.purchase_order_number].items.push(item);
                        return acc;
                      }, {})
                    ).map(([poNumber, { supplier, items }]) => (
                      <div key={poNumber} className="bg-white rounded-lg border border-gray-200 p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h4 className="font-semibold text-gray-900">{poNumber}</h4>
                            <p className="text-sm text-gray-500">Supplier: {supplier}</p>
                          </div>
                          <div className="text-sm text-gray-500">
                            {items.length} item(s)
                          </div>
                        </div>
                        
                        <div className="space-y-3">
                          {items.map((item) => (
                            <div key={item.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                              <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-center">
                                <div className="md:col-span-2">
                                  <h5 className="font-medium text-gray-900">{item.product_name}</h5>
                                  <p className="text-xs text-gray-500">ID: {item.product_id}</p>
                                </div>
                                
                                <div className="text-center">
                                  <p className="text-sm font-medium text-gray-900">
                                    {item.available_quantity.toFixed(2)} CTN
                                  </p>
                                  <p className="text-xs text-gray-500">({(item.available_quantity * item.pieces_in_ctn).toFixed(0)} pcs)</p>
                                </div>
                                
                                <div className="text-center">
                                  <p className="text-sm font-medium text-gray-900">{formatCurrency(item.unit_cost)}</p>
                                  <p className="text-xs text-gray-500">Unit Cost</p>
                                </div>
                                
                                <div className="flex items-center space-x-2">
                                  <div className="flex items-center space-x-1">
                                    <label className="text-xs font-medium text-gray-700">PCS:</label>
                                    <input
                                      type="number"
                                      min="0"
                                      value={item.qty_pcs}
                                      onChange={(e) => handleBulkReturnQuantityChange(item.id, 'qty_pcs', parseInt(e.target.value) || 0)}
                                      className="w-12 px-1 py-1 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                    />
                                  </div>
                                  <div className="flex items-center space-x-1">
                                    <label className="text-xs font-medium text-gray-700">CTN:</label>
                                    <input
                                      type="number"
                                      min="0"
                                      value={item.qty_ctn}
                                      onChange={(e) => handleBulkReturnQuantityChange(item.id, 'qty_ctn', parseInt(e.target.value) || 0)}
                                      className="w-12 px-1 py-1 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                    />
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    ({item.pieces_in_ctn})
                                  </div>
                                </div>
                                
                                <div className="text-center">
                                  <p className={`text-sm font-medium ${(item.qty_pcs + (item.qty_ctn * item.pieces_in_ctn)) > (item.available_quantity * item.pieces_in_ctn) ? 'text-red-600' : 'text-blue-600'}`}>
                                    {item.total_qty?.toFixed(2) || '0.00'} CTN
                                  </p>
                                  <p className="text-xs text-gray-500">Total</p>
                                  {(item.qty_pcs + (item.qty_ctn * item.pieces_in_ctn)) > (item.available_quantity * item.pieces_in_ctn) && (
                                    <p className="text-xs text-red-500">Exceeds available</p>
                                  )}
                                </div>
                                
                                <div className="text-right">
                                  <p className="text-sm font-medium text-gray-900">{formatCurrency(item.total_cost)}</p>
                                  <p className="text-xs text-gray-500">Cost</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                    
                    {/* Return Summary */}
                    <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-lg p-4 border border-orange-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-gray-900">Return Summary</h4>
                          <p className="text-sm text-gray-600">
                            {bulkReturnItems.filter(item => item.return_quantity > 0).length} item(s) selected for return
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-orange-600">
                            {formatCurrency(bulkReturnItems.reduce((sum, item) => sum + item.total_cost, 0))}
                          </p>
                          <p className="text-sm text-gray-500">Total Return Value</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Package size={48} className="mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-500">No items found in selected purchases</p>
                  </div>
                )}
              </div>

              {/* Warning Message */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <X size={14} className="text-white" />
                  </div>
                  <div>
                    <h4 className="font-medium text-yellow-800 mb-1">Important Notice</h4>
                    <p className="text-sm text-yellow-700">
                      This action will reduce the quantities of the selected items in your inventory. 
                      The remaining items will stay in your stock. Make sure you have selected the correct 
                      items and quantities before proceeding.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 p-6 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                <span className="font-medium">
                  {bulkReturnItems.filter(item => item.return_quantity > 0).length}
                </span> item(s) will be returned from <span className="font-medium">{selectedPurchases.length}</span> purchase(s)
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleCloseBulkReturnModal}
                  disabled={bulkReturnLoading}
                  className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-xl flex items-center gap-2 font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <X size={16} />
                  Cancel
                </button>
                <button
                  onClick={handleBulkReturn}
                  disabled={
                    bulkReturnLoading || 
                    !returnReason.trim() || 
                    bulkReturnItems.filter(item => item.return_quantity > 0).length === 0 ||
                    bulkReturnItems.some(item => (item.qty_pcs + (item.qty_ctn * item.pieces_in_ctn)) > (item.available_quantity * item.pieces_in_ctn))
                  }
                  className="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white rounded-xl flex items-center gap-2 font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {bulkReturnLoading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Returning...
                    </>
                  ) : (
                    <>
                      <RotateCcw size={16} />
                      Confirm Return
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Individual Return Modal */}
      {showIndividualReturnModal && selectedPurchaseForReturn && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white p-6 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <RotateCcw size={24} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Return Purchase Items</h2>
                  <p className="text-orange-100 text-sm">
                    PO: {selectedPurchaseForReturn.purchase_order_number} | 
                    Supplier: {selectedPurchaseForReturn.supplier?.name || 'Unknown'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleCloseIndividualReturnModal}
                className="p-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors"
              >
                <CloseIcon size={20} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Return Reason */}
              <div className="mb-6">
                <label htmlFor="individualReturnReason" className="block text-sm font-medium text-gray-700 mb-2">
                  Return Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="individualReturnReason"
                  value={returnReason}
                  onChange={(e) => setReturnReason(e.target.value)}
                  placeholder="Please provide a detailed reason for returning these items..."
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 resize-none"
                />
              </div>

              {/* Items Selection */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Items to Return</h3>
                <div className="space-y-4">
                  {returnItems.map((item) => (
                    <div key={item.id} className="bg-white rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{item.product_name}</h4>
                          <p className="text-sm text-gray-500">
                            Available: {item.available_quantity.toFixed(2)} CTN ({(item.available_quantity * item.pieces_in_ctn).toFixed(0)} pcs) | 
                            Unit Cost: {formatCurrency(item.unit_cost)}
                          </p>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-2">
                            <label className="text-sm font-medium text-gray-700">PCS:</label>
                            <input
                              type="number"
                              min="0"
                              value={item.qty_pcs}
                              onChange={(e) => handleReturnQuantityChange(item.id, 'qty_pcs', parseInt(e.target.value) || 0)}
                              className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            />
                          </div>
                          <div className="flex items-center space-x-2">
                            <label className="text-sm font-medium text-gray-700">CTN:</label>
                            <input
                              type="number"
                              min="0"
                              value={item.qty_ctn}
                              onChange={(e) => handleReturnQuantityChange(item.id, 'qty_ctn', parseInt(e.target.value) || 0)}
                              className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            />
                          </div>
                          <div className="text-xs text-gray-500">
                            ({item.pieces_in_ctn} pcs/ctn)
                          </div>
                          <div className="text-center">
                            <p className={`text-sm font-medium ${(item.qty_pcs + (item.qty_ctn * item.pieces_in_ctn)) > (item.available_quantity * item.pieces_in_ctn) ? 'text-red-600' : 'text-blue-600'}`}>
                              {item.total_qty?.toFixed(2) || '0.00'} CTN
                            </p>
                            <p className="text-xs text-gray-500">Total</p>
                            {(item.qty_pcs + (item.qty_ctn * item.pieces_in_ctn)) > (item.available_quantity * item.pieces_in_ctn) && (
                              <p className="text-xs text-red-500">Exceeds available</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium text-gray-900">
                              {formatCurrency(item.total_cost)}
                            </p>
                            <p className="text-xs text-gray-500">Total Cost</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Return Summary */}
                <div className="mt-6 bg-gradient-to-r from-orange-50 to-red-50 rounded-lg p-4 border border-orange-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-gray-900">Return Summary</h4>
                      <p className="text-sm text-gray-600">
                        {returnItems.filter(item => item.return_quantity > 0).length} item(s) selected for return
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-orange-600">
                        {formatCurrency(returnItems.reduce((sum, item) => sum + item.total_cost, 0))}
                      </p>
                      <p className="text-sm text-gray-500">Total Return Value</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Warning Message */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <X size={14} className="text-white" />
                  </div>
                  <div>
                    <h4 className="font-medium text-yellow-800 mb-1">Important Notice</h4>
                    <p className="text-sm text-yellow-700">
                      This action will reduce the quantities of the selected items in your inventory. 
                      The remaining items will stay in your stock. Make sure you have selected the correct 
                      items and quantities before proceeding.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 p-6 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                <span className="font-medium">
                  {returnItems.filter(item => item.return_quantity > 0).length}
                </span> item(s) will be returned
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleCloseIndividualReturnModal}
                  className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-xl flex items-center gap-2 font-semibold transition-all duration-200"
                >
                  <X size={16} />
                  Cancel
                </button>
                <button
                  onClick={handleIndividualReturn}
                  disabled={
                    !returnReason.trim() || 
                    returnItems.filter(item => item.return_quantity > 0).length === 0 ||
                    returnItems.some(item => (item.qty_pcs + (item.qty_ctn * item.pieces_in_ctn)) > (item.available_quantity * item.pieces_in_ctn))
                  }
                  className="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white rounded-xl flex items-center gap-2 font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RotateCcw size={16} />
                  Confirm Return
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ReturnPurchases;
